// ClientBookingReports.js
import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import "./clientBookingReports.css";
import { DateTime } from "luxon";
import { formatDubai } from "../../../../utils/dubaiTime";

const isVideoFile = (url) => /\.(mp4|mov|avi|mkv)$/i.test(url || "");

const ClientBookingReports = ({ bookingId }) => {
    const [reports, setReports] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [noReports, setNoReports] = useState(false);

    const [expired, setExpired] = useState(false);
    const [bookingStartIso, setBookingStartIso] = useState(null);

    // Single source of truth for the viewer: index into the reports array.
    const [selectedIndex, setSelectedIndex] = useState(null);
    const currentReport =
        selectedIndex != null && reports[selectedIndex] ? reports[selectedIndex] : null;

    const formatReportType = (type) => {
        const typeMappings = {
            before_cleaning_video: "Before Cleaning Video",
            after_cleaning_video: "After Cleaning Video",
            damages_apartment_condition: "Damage / Unit Condition",
            stained_laundry_pickup: "Stained Laundry Pickup",
            damages: "Damages",
            apartment_condition: "Unit Condition",
            task_report: "Task Report",
        };
        return typeMappings[type] || "Other Report";
    };

    const formatDate = (dateString) => {
        const dubaiTime = DateTime.fromISO(dateString, { zone: "Asia/Dubai" });
        return dubaiTime.toFormat("dd LLL yyyy - HH:mm");
    };

    const isOlderThan60Days = useMemo(() => {
        if (!bookingStartIso) return false;
        const bookingStart = DateTime.fromISO(bookingStartIso, { zone: "utc" }).setZone("Asia/Dubai");
        if (!bookingStart.isValid) return false;
        const diffDays = DateTime.now().setZone("Asia/Dubai").diff(bookingStart, "days").days;
        return diffDays > 60;
    }, [bookingStartIso]);

    useEffect(() => {
        let cancelled = false;

        const load = async () => {
            setLoading(true);
            setError("");
            setNoReports(false);
            setExpired(false);
            setReports([]);

            try {
                const bookingRes = await axios.get(`/api/client-requests/bookings/${bookingId}`, { withCredentials: true });
                if (cancelled) return;

                const startIso = bookingRes?.data?.booking_start || null;
                setBookingStartIso(startIso);

                if (startIso) {
                    const bookingStart = DateTime.fromISO(startIso, { zone: "utc" }).setZone("Asia/Dubai");
                    const diffDays = DateTime.now().setZone("Asia/Dubai").diff(bookingStart, "days").days;

                    if (Number.isFinite(diffDays) && diffDays > 60) {
                        setExpired(true);
                        setLoading(false);
                        return;
                    }
                }

                const response = await axios.get(`/api/client-requests/bookings/${bookingId}/reports`, { withCredentials: true });
                if (cancelled) return;

                // Task reports linked to a task are shown under that task in the
                // Tasks section — this section lists everything else, including
                // task reports that were uploaded without a task link (they would
                // otherwise be invisible to the client).
                const others = (Array.isArray(response.data) ? response.data : [])
                    .filter((r) => !(r.type === 'task_report' && r.task_id != null));
                if (others.length === 0) {
                    setNoReports(true);
                } else {
                    setReports(others);
                }
            } catch (err) {
                if (cancelled) return;

                if (err?.response?.status === 404) {
                    setNoReports(true);
                } else {
                    setError("Failed to fetch booking reports.");
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        load();
        return () => {
            cancelled = true;
        };
    }, [bookingId]);

    const openAt = (index) => {
        setSelectedIndex(index);
    };

    const handleClose = useCallback(() => {
        setSelectedIndex(null);
    }, []);

    const goPrev = useCallback(() => {
        setSelectedIndex((i) => (i == null || i <= 0 ? i : i - 1));
    }, []);

    const goNext = useCallback(() => {
        setSelectedIndex((i) =>
            i == null || i >= reports.length - 1 ? i : i + 1
        );
    }, [reports.length]);

    // Keyboard shortcuts while the viewer is open.
    useEffect(() => {
        if (selectedIndex == null) return undefined;

        const onKey = (e) => {
            if (e.key === "Escape") {
                e.preventDefault();
                handleClose();
            } else if (e.key === "ArrowLeft") {
                e.preventDefault();
                goPrev();
            } else if (e.key === "ArrowRight") {
                e.preventDefault();
                goNext();
            }
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [selectedIndex, handleClose, goPrev, goNext]);

    // When navigating to a video, auto-play it.
    useEffect(() => {
        if (selectedIndex == null || !currentReport) return;
        if (!isVideoFile(currentReport.file_url)) return;
        const id = setTimeout(() => {
            const video = document.querySelector(".client-report-full-video");
            if (video) {
                try {
                    video.currentTime = 0;
                    video.play();
                } catch {
                    /* ignore autoplay restrictions */
                }
            }
        }, 80);
        return () => clearTimeout(id);
    }, [selectedIndex, currentReport]);

    if (loading)
        return (
            <div className="app-loading">
                <div className="app-spinner" />
                <div className="app-spinner-text">Loading Reports...</div>
            </div>
        );

    if (error) return <div className="client-error-message">{error}</div>;

    if (expired || isOlderThan60Days) {
        return (
            <div className="client-reports-container">
                <div className="client-reports-empty client-reports-empty--expired">
                    <div className="client-reports-empty__icon">⏳</div>
                    <div className="client-reports-empty__title">Booking report attachments are no longer available.</div>
                    <div className="client-reports-empty__text">
                        Booking report attachments are available for 60 days from the date of upload.
                    </div>
                </div>
            </div>
        );
    }

    if (noReports) {
        return (
            <div className="client-reports-container">
                <div className="client-reports-empty">
                    <div className="client-reports-empty__icon">📎</div>
                    <div className="client-reports-empty__title">No reports available</div>
                    <div className="client-reports-empty__text">
                        Reports will appear here once our team uploads them for this booking.
                    </div>
                </div>
            </div>
        );
    }

    const hasPrev = selectedIndex != null && selectedIndex > 0;
    const hasNext = selectedIndex != null && selectedIndex < reports.length - 1;

    return (
        <div className="client-reports-container">
            <p className="client-30-days">Please note attachments are only available for 60 days from date of upload.</p>

            <div className="client-reports-grid">
                {reports.map((report, idx) => (
                    <div key={report.id} className="client-report-card" onClick={() => openAt(idx)}>
                        <div className="client-report-thumbnail">
                            {isVideoFile(report.file_url) ? (
                                <div className="client-view-thumbnail-wrapper">
                                    <img
                                        src={report.thumbnail_url || report.file_url.replace(/\.mp4$/, ".jpg")}
                                        alt="Video Thumbnail"
                                        className="client-report-image-preview"
                                        loading="lazy"
                                    />
                                    <div className="client-view-play-button-overlay">
                                        <span>&#9658;</span>
                                    </div>
                                </div>
                            ) : (
                                <img
                                    src={report.file_url}
                                    alt="Report Attachment"
                                    className="client-report-image-preview"
                                    loading="lazy"
                                />
                            )}
                        </div>

                        <div className="client-report-body">
                            <p className="client-report-type">{formatReportType(report.type)}</p>
                            {report.type === "task_report" && report.task_instruction && (
                                <p className="client-report-task">{report.task_instruction}</p>
                            )}
                            <p className="client-report-date">{formatDate(report.uploaded_at)}</p>
                            {report.comment && (
                                <p className="client-report-comment">{report.comment}</p>
                            )}
                        </div>
                    </div>
                ))}
            </div>

            {currentReport && (
                <div
                    className="client-report-modal"
                    onClick={(e) => {
                        // Close when clicking the dim backdrop, not the content.
                        if (e.target === e.currentTarget) handleClose();
                    }}
                >
                    {reports.length > 1 && (
                        <div className="client-report-counter">
                            {selectedIndex + 1} / {reports.length}
                        </div>
                    )}
                    <button type="button" className="modal-x client-report-close" onClick={handleClose}>
                        &times;
                    </button>

                    {hasPrev && (
                        <button
                            type="button"
                            className="client-report-nav client-report-nav--prev"
                            onClick={goPrev}
                            aria-label="Previous report"
                        >
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                                <polyline points="15 6 9 12 15 18" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        </button>
                    )}

                    <div className="client-report-modal-content" onClick={(e) => e.stopPropagation()}>
                        {isVideoFile(currentReport.file_url) ? (
                            <video
                                key={currentReport.id}
                                controls
                                className="client-report-full-video"
                                autoPlay
                            >
                                <source src={currentReport.file_url} type="video/mp4" />
                            </video>
                        ) : (
                            <img
                                key={currentReport.id}
                                src={currentReport.file_url}
                                alt="Full Report Attachment"
                                className="client-report-full-image"
                            />
                        )}

                        <div className="client-report-modal-info">
                            <p>
                                <strong>
                                    {/* Asia/Dubai render (item 8) — matches the
                                        staff BookingReports format */}
                                    {formatDubai(currentReport.uploaded_at, 'dd LLLL yyyy, HH:mm')}
                                </strong>
                            </p>
                            <p>
                                <strong>Report Type:</strong> {formatReportType(currentReport.type)}
                            </p>
                            <p>
                                <strong>Comment:</strong> {currentReport.comment || "N/A"}
                            </p>
                        </div>
                    </div>

                    {hasNext && (
                        <button
                            type="button"
                            className="client-report-nav client-report-nav--next"
                            onClick={goNext}
                            aria-label="Next report"
                        >
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                                <polyline points="9 6 15 12 9 18" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        </button>
                    )}
                </div>
            )}
        </div>
    );
};

export default ClientBookingReports;
